import React, { useState, useCallback } from "react";
import MainCalculatorsList from "./MainCalculators";
import SharedHelmet from "../Helmet";
import RelatedLinks from "../RelatedLinks";
import { FirstAds, SecondAds,MobileUnderCalAds } from "../../Ads";
const TITLE = "Calculatrice d'onde - Calculez la longueur d'onde, la fréquence ou la vitesse d'onde";
const DESCRIPTION =
  "Calculatrice d'onde - Calculez la longueur d'onde, la fréquence ou la vitesse d'onde. Calculateur de physique en francais avec resultat rapide, unites claires et aide pratique pour exercices et revisions.";
const KEYWORDS =
  "wave, calculatrice physique, formule physique, exercices de physique, calcul en ligne";
const OG_URL = "https://do-calculate.com/calculator/fr/wave";
function WaveCalculator() {
  const [wavelength, setWavelength] = useState("");
  const [frequency, setFrequency] = useState("");
  const [waveSpeed, setWaveSpeed] = useState("");
  const [calculationMode, setCalculationMode] = useState("calculateWavelength");
  const [error, setError] = useState("");
  const [showSteps, setShowSteps] = useState(false);
  const updateCalculations = useCallback(() => {
    setError("");
    const wavelengthValue = parseFloat(wavelength) || 0;
    const frequencyValue = parseFloat(frequency) || 0;
    const waveSpeedValue = parseFloat(waveSpeed) || 0;
    switch (calculationMode) {
      case "calculateWavelength":
        if (waveSpeedValue > 0 && frequencyValue > 0) {
          const calculatedWavelength = waveSpeedValue / frequencyValue;
          setWavelength(Math.round(calculatedWavelength * 100) / 100);
        } else {
          setError("Veuillez entrer la vitesse d'onde et la fréquence correctement");
        }
        break;
      case "calculateFrequency":
        if (waveSpeedValue > 0 && wavelengthValue > 0) {
          const calculatedFrequency = waveSpeedValue / wavelengthValue;
          setFrequency(Math.round(calculatedFrequency * 100) / 100);
        } else {
          setError("Veuillez entrer la vitesse d'onde et la longueur d'onde correctement");
        }
        break;
      case "calculateWaveSpeed":
        if (wavelengthValue > 0 && frequencyValue > 0) {
          const calculatedWaveSpeed = wavelengthValue * frequencyValue;
          setWaveSpeed(Math.round(calculatedWaveSpeed * 100) / 100);
        } else {
          setError("Veuillez entrer la longueur d'onde et la fréquence correctement");
        }
        break;
      default:
        break;
    }
  }, [wavelength, frequency, waveSpeed, calculationMode]);
  const handleCalculateClick = () => {
    updateCalculations();
    setShowSteps(true);
  };
  const handleWavelengthChange = (e) => setWavelength(e.target.value);
  const handleFrequencyChange = (e) => setFrequency(e.target.value);
  const handleWaveSpeedChange = (e) => setWaveSpeed(e.target.value);
  const handleModeChange = (e) => setCalculationMode(e.target.value);
  const getSolutionSteps = () => {
    switch (calculationMode) {
      case "calculateWavelength":
        return (
          <div className="alert alert-info">
            <h4>Étapes de calcul de la longueur d'onde :</h4>
            <ol>
              <li>Entrez la vitesse d'onde (en m/s) dans le champ dédié.</li>
              <li>Entrez la fréquence (en Hz) dans le champ dédié.</li>
              <li>Cliquez sur le bouton "Calculer" pour obtenir la longueur d'onde.</li>
              <li>Longueur d'onde = Vitesse d'onde / Fréquence.</li>
            </ol>
          </div>
        );
      case "calculateFrequency":
        return (
          <div className="alert alert-info">
            <h4>Étapes de calcul de la fréquence :</h4>
            <ol>
              <li>Entrez la vitesse d'onde (en m/s) dans le champ dédié.</li>
              <li>Entrez la longueur d'onde (en m) dans le champ dédié.</li>
              <li>Cliquez sur le bouton "Calculer" pour obtenir la fréquence.</li>
              <li>Fréquence = Vitesse d'onde / Longueur d'onde.</li>
            </ol>
          </div>
        );
      case "calculateWaveSpeed":
        return (
          <div className="alert alert-info">
            <h4>Étapes de calcul de la vitesse d'onde :</h4>
            <ol>
              <li>Entrez la longueur d'onde (en m) dans le champ dédié.</li>
              <li>Entrez la fréquence (en Hz) dans le champ dédié.</li>
              <li>Cliquez sur le bouton "Calculer" pour obtenir la vitesse d'onde.</li>
              <li>Vitesse d'onde = Longueur d'onde × Fréquence.</li>
            </ol>
          </div>
        );
      default:
        return null;
    }
  };
  return (
    <div className="ltr">
      <SharedHelmet
        TITLE={TITLE}
        DESCRIPTION={DESCRIPTION}
        KEYWORDS={KEYWORDS}
        OG_URL={OG_URL}
      />
      <div className="row">
        <div className="col-12 col-md-8">
          <FirstAds />
        </div>
      </div>
      <div className="row">
        <div className="col-12 col-md-8">
          <br />
          <div
            className="bg-success text-white text-center"
            style={{ height: "50px" }}
          >
            <h1 style={{ fontSize: "17px" }}>{TITLE}</h1>
          </div>
          <div className="border border-light">
            <div className="row">
              <div>
                <p>
                  La calculatrice d'onde vous permet d'effectuer trois types de calculs en fonction des valeurs entrées. Choisissez le type de calcul approprié et entrez les valeurs disponibles pour calculer la valeur manquante avec précision et facilité.
                </p>
                <h2 style={{ fontSize: "17px" }}>
                  Trouver la longueur d'onde en connaissant la vitesse d'onde et la fréquence
                </h2>
                <p>
                  Si vous connaissez la vitesse d'onde (en m/s) et la fréquence (en Hz), vous pouvez calculer la longueur d'onde (en m) en utilisant la formule suivante :
                </p>
                <p>
                  <b>Longueur d'onde = Vitesse d'onde / Fréquence</b>
                </p>
              </div>
            </div>
            <MobileUnderCalAds />
            <div className="row">
              <div className="col-sm-12 col-md-7 calculatorStyle">
                <div className="p-3 mb-2 bg-success text-white">
                  <h2 style={{ fontSize: "16px" }}>{TITLE} </h2>
                </div>
                <hr />
                <div dir="ltr">
                  <div>
                    <div className="form-group">
                      <b>
                        <label>Mode de calcul :</label>
                      </b>
                      <select
                        value={calculationMode}
                        onChange={handleModeChange}
                        className="form-control"
                      >
                        <option value="calculateWavelength">Calculer la longueur d'onde</option>
                        <option value="calculateFrequency">Calculer la fréquence</option>
                        <option value="calculateWaveSpeed">Calculer la vitesse d'onde</option>
                      </select>
                    </div>
                    <br />
                    <div className="form-group d-flex align-items-center">
                      <b>
                        <label className="form-label ms-2">Longueur d'onde (m)</label>
                      </b>
                      <input
                        type="number"
                        placeholder="0"
                        value={wavelength}
                        onChange={handleWavelengthChange}
                        className="form-control"
                        style={{ width: "150px" }}
                      />
                    </div>
                    <br />
                    <div className="form-group d-flex align-items-center">
                      <b>
                        <label className="form-label ms-2">Fréquence (Hz)</label>
                      </b>
                      <input
                        type="number"
                        placeholder="0"
                        value={frequency}
                        onChange={handleFrequencyChange}
                        className="form-control"
                        style={{ width: "150px" }}
                      />
                    </div>
                    <br />
                    <div className="form-group d-flex align-items-center">
                      <b>
                        <label className="form-label ms-2">Vitesse d'onde (m/s)</label>
                      </b>
                      <input
                        type="number"
                        placeholder="0"
                        value={waveSpeed}
                        onChange={handleWaveSpeedChange}
                        className="form-control"
                        style={{ width: "150px" }}
                      />
                    </div>
                    <br />
                    <button
                      className="btn btn-success"
                      style={{ fontSize: "20px" }}
                      onClick={handleCalculateClick}
                    >
                      Calculer
                    </button>
                  </div>
                  <br />
                  <div>
                    {error !== "" ? (
                      <p className="p-3 mb-2 bg-warning text-white">
                        Erreur : {error}
                      </p>
                    ) : null}
                    {showSteps && getSolutionSteps()}
                  </div>
                </div>
              </div>
            </div>
            <hr />
            <div className="row">
              <RelatedLinks />
              <hr />
            </div>
          </div>
        </div>
        <div className="col">
          <SecondAds />
          <br />
          <MainCalculatorsList></MainCalculatorsList>
          <br />
        </div>
      </div>
      <div className="row" style={{ marginTop: "20px" }}>
        <div className="col-12 col-md-8">
          <div className="info-sections-container">
            <div className="info-section">
              <div className="info-section-header"><h3 style={{ fontSize: "16px" }}>Formule des ondes a retenir</h3></div>
              <div className="info-section-body">
                <p>La relation fondamentale est v = lambda × f, ou v est la vitesse d'onde, lambda la longueur d'onde et f la frequence. Cette equation permet de retrouver n'importe quelle grandeur si les deux autres sont connues.</p>
              </div>
            </div>
            <div className="info-section">
              <div className="info-section-header"><h3 style={{ fontSize: "16px" }}>Applications concretes</h3></div>
              <div className="info-section-body">
                <p>Ce calcul est utilise en acoustique (sons), en optique (lumiere) et en telecoms. Il aide a comprendre comment une variation de frequence modifie la longueur d'onde dans un meme milieu.</p>
              </div>
            </div>
            <div className="info-section">
              <div className="info-section-header"><h3 style={{ fontSize: "16px" }}>Conseils de saisie</h3></div>
              <div className="info-section-body">
                <p>Vérifiez les unites: metres pour la longueur d'onde, hertz pour la frequence et m/s pour la vitesse. Une incoherence d'unite est la source la plus courante d'erreur sur ce type de calcul.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
      {" "}
      <br />
    </div>
  );
}
export default WaveCalculator;
